import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ArrowDown, ArrowUp } from '@travel/icons/ui';
import {
  MOBILE_SCREEN,
  useDeviceType,
  useIsInViewport,
  useKeyboard,
} from '@travel/traveler-core/hooks';
import { cx } from '@travel/utils';

import { Translate } from '../../../../core/translate';

import styles from './basicInformation.module.scss';

export default function PropertyDescription(props: { description: string }) {
  const { description } = props;
  if (!description) {
    return null;
  }

  const isMobile = useDeviceType() === MOBILE_SCREEN;
  const LINES_LIMIT = isMobile ? 3 : 8;
  const descriptionRef = useRef<HTMLParagraphElement | null>(null);
  const isDescriptionInViewport = useIsInViewport(descriptionRef);
  // state that governs if description is expanded (not truncated/clamped) or not
  // is set to true by default since:
  //   the logic for calculating if element is beyond line limit
  //   requires it to not be clamped by default
  // also handles button appearance
  const [isExpanded, setIsExpanded] = useState(true);
  // governs if show more/less button is rendered or not
  // shouldShowExpandBtn = true => description is within line count limit
  const [shouldShowExpandBtn, setShouldShowExpandBtn] = useState(false);

  const onTruncate = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent<Element>) => {
      event.preventDefault();
      setIsExpanded(!isExpanded);
    },
    [isExpanded],
  );

  useEffect(() => {
    if (descriptionRef.current) {
      const lineHeight = Number(
        window
          .getComputedStyle(descriptionRef.current, null)
          .getPropertyValue('line-height')
          .replace('px', '') || 1,
      );

      // description element's height > height of 8/3 individual lines
      // implying it needs to be truncated, and show more button is to be shown
      if (descriptionRef.current.getBoundingClientRect().height > lineHeight * LINES_LIMIT) {
        setIsExpanded(false);
        setShouldShowExpandBtn(true);
      }
    }
  }, [LINES_LIMIT, isDescriptionInViewport]);

  const onKeyDownInput = useKeyboard(
    useMemo(
      () => ({
        Submit: onTruncate,
      }),
      [onTruncate],
    ),
  );

  return (
    <div className={styles.descriptionWrapper}>
      <div
        tabIndex={0}
        role="button"
        className={cx(!isExpanded && styles.ellipsis)}
        ref={descriptionRef}
        onClick={onTruncate}
        onKeyDown={onKeyDownInput}
      >
        <p>{description}</p>
      </div>
      {shouldShowExpandBtn && (
        <button className={styles.showMoreLink} onClick={onTruncate}>
          <Translate
            data-testid="providerInformationPage-basicInformation-showMore"
            id={!isExpanded ? 'Traveler_Common.Show_More' : 'Traveler_Common.Show_Less'}
          />
          {isExpanded ? (
            <ArrowUp size={16} color="cilantro" />
          ) : (
            <ArrowDown size={16} color="cilantro" />
          )}
        </button>
      )}
    </div>
  );
}
